import { PageTypeGlobal, PageTypePortal } from "@/lib/types";
import { formatLocationAddressLines } from "@/lib/utils/formatLocationAddress";
import {
  buildGoogleMapsOpenUrl,
  buildGoogleMapsSearchQuery,
} from "@/lib/utils/googleMaps";
import type { GetTenantResponse } from "@/openapi/generated";

const ACCOUNT_HREFS = new Set<string>([
  PageTypePortal.Login,
  PageTypePortal.SignUp,
  PageTypePortal.Dashboard,
]);

const LEGAL_HREFS = new Set<string>([PageTypeGlobal.Terms]);

const ACCOUNT_ORDER = [
  PageTypePortal.SignUp,
  PageTypePortal.Login,
  PageTypePortal.Dashboard,
] as const;

function stripWww(host: string): string {
  return host.replace(/^www\./i, "").toLowerCase();
}

function isPlatformOrLocalHost(host: string): boolean {
  const h = stripWww(host);
  if (!h || h === "localhost" || h.startsWith("127.") || h === "::1") {
    return true;
  }
  return h === "beclassmanager.com" || h.endsWith(".beclassmanager.com");
}

export function normalizeHttpUrl(
  raw: string | null | undefined,
): string | null {
  const value = raw?.trim();
  if (!value) return null;

  const hasHttpProtocol = /^https?:\/\//i.test(value);
  const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);
  if (hasProtocol && !hasHttpProtocol && !value.startsWith("//")) {
    return null;
  }

  try {
    const withProtocol = hasHttpProtocol
      ? value
      : value.startsWith("//")
        ? `https:${value}`
        : `https://${value}`;
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    if (!parsed.hostname) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function isStudioWebsiteThisSite(
  siteUrl: string,
  currentOrigins: Array<string | null | undefined>,
): boolean {
  const normalized = normalizeHttpUrl(siteUrl);
  if (!normalized) return true;

  let host: string;
  try {
    host = new URL(normalized).hostname;
  } catch {
    return true;
  }

  if (isPlatformOrLocalHost(host)) return true;

  const hostNorm = stripWww(host);
  for (const origin of currentOrigins) {
    const originUrl = normalizeHttpUrl(origin);
    if (!originUrl) continue;
    try {
      if (stripWww(new URL(originUrl).hostname) === hostNorm) {
        return true;
      }
    } catch {
      // Invalid origin, skip
    }
  }

  return false;
}

/** Marketing website URL, or null when empty, invalid, or this microsite. */
export function getExternalStudioWebsiteUrl(
  siteUrl: string | null | undefined,
  currentOrigins: Array<string | null | undefined>,
): string | null {
  const normalized = normalizeHttpUrl(siteUrl);
  if (!normalized) return null;
  if (isStudioWebsiteThisSite(normalized, currentOrigins)) return null;
  return normalized;
}

export function getWebsiteHostnameLabel(url: string): string {
  try {
    return stripWww(new URL(url).hostname);
  } catch {
    return "Website";
  }
}

export function getStudioFooterName(
  tenant: GetTenantResponse | null | undefined,
): string {
  if (!tenant) return "";
  return (tenant.displayName || tenant.name || "").trim();
}

export function getStudioFooterEmail(
  email: string | null | undefined,
): string | null {
  const value = email?.trim() ?? "";
  if (!value.includes("@")) return null;
  return value;
}

export function getStudioFooterAddressLines(
  tenant: GetTenantResponse | null | undefined,
): string[] {
  if (!tenant) return [];
  return formatLocationAddressLines(tenant);
}

export function getStudioMapsOpenUrl(
  tenant: GetTenantResponse | null | undefined,
): string | null {
  if (!tenant) return null;
  const query = buildGoogleMapsSearchQuery({
    addressLine1: tenant.addressLine1,
    addressLine2: tenant.addressLine2,
    addressLine3: tenant.addressLine3,
    city: tenant.city,
    zipCode: tenant.zipCode,
    name: tenant.displayName || tenant.name,
  });
  return query ? buildGoogleMapsOpenUrl(query) : null;
}

export function getPublicFooterHomeHref(): string {
  return "/";
}

export function partitionPublicFooterLinks<T extends { href: string }>(
  links: T[],
): {
  explore: T[];
  account: T[];
  legal: T[];
} {
  const explore: T[] = [];
  const account: T[] = [];
  const legal: T[] = [];

  for (const link of links) {
    if (ACCOUNT_HREFS.has(link.href)) {
      account.push(link);
    } else if (LEGAL_HREFS.has(link.href)) {
      legal.push(link);
    } else {
      explore.push(link);
    }
  }

  account.sort((a, b) => {
    const ai = ACCOUNT_ORDER.indexOf(a.href as (typeof ACCOUNT_ORDER)[number]);
    const bi = ACCOUNT_ORDER.indexOf(b.href as (typeof ACCOUNT_ORDER)[number]);
    return (
      (ai === -1 ? ACCOUNT_ORDER.length : ai) -
      (bi === -1 ? ACCOUNT_ORDER.length : bi)
    );
  });

  return { explore, account, legal };
}
